import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';

@Component({
    standalone: true,
    selector: 'abs-chip',
    imports: [CommonModule, RouterModule, IonicModule],
    template: `
        <ng-template #chipBody>
            <span class="chip-content">
                <span class="chip-icon">
                    <ng-content select="ion-icon"></ng-content>
                </span>
                <span class="chip-label">
                    <ng-content></ng-content>
                </span>
            </span>
        </ng-template>

        @if (removable) {
            <div [class]="chipClass()" (click)="onChipClick($event)">
                <ng-container [ngTemplateOutlet]="chipBody"></ng-container>
                <button type="button" class="action-chip-remove" (click)="onRemove($event)">
                    <ion-icon name="close-outline"></ion-icon>
                </button>
            </div>
        } @else if (routerLink) {
            <a [routerLink]="routerLink" [class]="chipClass()" (click)="onChipClick($event)">
                <ng-container [ngTemplateOutlet]="chipBody"></ng-container>
            </a>
        } @else if (asButton) {
            <button type="button" [class]="chipClass()" (click)="onChipClick($event)">
                <ng-container [ngTemplateOutlet]="chipBody"></ng-container>
            </button>
        } @else {
            <span [class]="chipClass()">
                <ng-container [ngTemplateOutlet]="chipBody"></ng-container>
            </span>
        }
    `,
    styles: [
        `
            :host {
                display: inline-flex;
            }

            .info-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                font-size: 0.85em;
                font-weight: 500;
                line-height: 1.2;
                color: #444;
                background: rgba(0, 0, 0, 0.04);
                border: 1px solid rgba(0, 0, 0, 0.1);
                border-radius: 4px;
                padding: 3px 8px;
                text-decoration: none;
                white-space: nowrap;
                transition:
                    background 0.15s ease,
                    border-color 0.15s ease;
            }

            /* Ensure projected text content in chips always remains visible */
            .info-chip > span,
            .action-chip > span,
            .chip-content > span,
            .chip-label {
                color: inherit;
                opacity: 1;
                font: inherit;
            }

            /* Reset native button styles so button chips match link/span chips */
            a.info-chip,
            a.action-chip,
            button.info-chip,
            button.action-chip {
                appearance: none;
                -webkit-appearance: none;
                font: inherit;
                font-size: 0.72em;
                line-height: inherit;
                text-align: inherit;
                color: inherit;
                margin: 0;
                box-shadow: none;
                outline: none;
                cursor: pointer;
            }

            .info-chip ion-icon {
                font-size: 0.95em;
                color: #888;
            }

            .chip-icon {
                display: inline-flex;
                align-items: center;
                flex: 0 0 auto;
            }

            .chip-icon:empty {
                display: none;
            }

            .chip-icon ion-icon {
                font-size: 0.95em;
                color: #888;
            }

            .info-chip:hover {
                background: rgba(0, 0, 0, 0.08);
                border-color: rgba(0, 0, 0, 0.18);
            }

            .info-chip.primary {
                color: var(--btn-color, #00a03e);
                background: rgba(var(--btn-color-rgb, 0, 160, 62), 0.06);
                border-color: rgba(var(--btn-color-rgb, 0, 160, 62), 0.2);
            }

            .info-chip.primary ion-icon {
                color: var(--btn-color, #00a03e);
            }

            .info-chip.primary:hover {
                background: rgba(var(--btn-color-rgb, 0, 160, 62), 0.12);
                border-color: rgba(var(--btn-color-rgb, 0, 160, 62), 0.35);
            }

            .action-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                font-size: 0.72em;
                font-weight: 500;
                padding: 2px 6px 2px 8px;
                border-radius: 4px;
                background: rgba(var(--btn-color-rgb, 0, 160, 62), 0.08);
                border: 1px solid rgba(var(--btn-color-rgb, 0, 160, 62), 0.2);
                color: var(--btn-color, #00a03e);
                white-space: nowrap;
            }

            .chip-content {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                font-size: inherit;
            }

            .action-chip-remove {
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
                width: 16px !important;
                height: 16px !important;
                min-width: unset !important;
                min-height: unset !important;
                padding: 0 !important;
                border: none !important;
                border-radius: 0 !important;
                background: none !important;
                box-shadow: none !important;
                color: inherit !important;
                cursor: pointer;
                font-size: 1.1em !important;
                margin-right: -4px !important;
                opacity: 0.6;
                transition: opacity 0.15s;
            }

            .action-chip-remove:hover {
                opacity: 1;
            }
        `
    ]
})
export class AbsChipComponent {
    @Input() appearance: 'info' | 'action' = 'info';
    @Input() primary = false;
    @Input() removable = false;
    @Input() asButton = false;
    @Input() routerLink: string | any[] | null = null;
    @Input() cssClass = '';

    @Output() chipClick = new EventEmitter<MouseEvent>();
    @Output() remove = new EventEmitter<void>();

    chipClass(): string {
        const base = this.removable || this.appearance === 'action' ? 'action-chip' : 'info-chip';
        const primary = this.primary ? 'primary' : '';
        return [base, primary, this.cssClass].filter(Boolean).join(' ');
    }

    onChipClick(event: MouseEvent): void {
        this.chipClick.emit(event);
    }

    onRemove(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.remove.emit();
    }
}
